import { ArnTrackingService } from './ArnTrackingService';
import {
  ArnTrackerConfig,
  ArnTrackerType,
  ArnTrackingConfig,
  ArnValidTrackingConfig,
  GoogleTrackingConfig,
  MatomoTrackingConfig,
  SentryTrackingConfig,
} from '@arianee/arn-types';
import { ArnTrackingServiceFactory } from './ArnTrackingServiceFactory';
import { SentryTrackingService } from './sentry/SentryTrackingService';
import { GoogleAnalyticsService } from './google/GoogleAnalyticsService';
import { MatomoTrackingService } from './matomo/MatomoTrackingService';
import { ArnClientError } from '../ArnClientError';
import { DefaultArnTrackingService } from './DefaultArnTrackingService';

/**
 * This default implementation is a composite of ArnTrackingServices:
 * it will propagate events for each configured tracker.
 */
export class DefaultArnTrackingServiceFactory
  implements ArnTrackingServiceFactory
{
  create(spec?: ArnTrackingConfig): ArnTrackingService {
    const config = this.createConfig(spec);
    let trackingService: ArnTrackingService = {} as ArnTrackingService;
    for (const trackerConfig of config.trackers) {
      trackingService = {
        ...trackingService,
        ...this.createTracker(trackerConfig),
      };
    }
    return new DefaultArnTrackingService(trackingService);
  }

  createTracker(trackerConfig: ArnTrackerConfig): ArnTrackingService {
    const trackingService: ArnTrackingService = {} as ArnTrackingService;

    switch (trackerConfig.arnTrackerType) {
      case ArnTrackerType.sentry:
        trackingService.sentry = new SentryTrackingService(
          trackerConfig as SentryTrackingConfig
        );
        break;
      case ArnTrackerType.google:
        trackingService.googleAnalytics = new GoogleAnalyticsService(
          trackerConfig as GoogleTrackingConfig
        );
        break;
      case ArnTrackerType.matomo:
        trackingService.matomo = new MatomoTrackingService(
          trackerConfig as MatomoTrackingConfig
        );
        break;
      case ArnTrackerType.facebook:
      default:
        throw new ArnClientError(
          `Tracker type "${trackerConfig.arnTrackerType}" is not supported by this ARN Client`
        );
    }

    return trackingService;
  }

  createConfig(spec?: ArnTrackingConfig): ArnValidTrackingConfig {
    return spec || { trackers: [] };
  }
}
